import { jsPDF as PDFDocument } from 'jspdf'
import { withCdn } from '@/app/utils/withCdn'
import { useOS } from '../hooks/useOS'
import { CouponItem, raffleService } from '../services/raffleService'

interface PDFGeneratorProps {
  userId: string
  raffleId: string
}

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

interface generatePDFProps {
  raffleBanner: string
  raffleTitle: string
  raffleDescription: string
  raffleHash: string
  coupons: Array<{ id: string; code: string }> | CouponItem[]
  isMobile: boolean
  fileName?: string
}

const generatePDF = async ({
  raffleBanner,
  raffleTitle,
  raffleDescription,
  raffleHash,
  coupons,
  isMobile,
  fileName = 'sorteio.pdf',
}: generatePDFProps) => {
  try {
    const pdf = new PDFDocument({
      format: 'a4',
      orientation: 'portrait',
      unit: 'mm',
    })

    const bannerImage = await fetch(withCdn(raffleBanner))
      .then((res) => res.blob())
      .then((blob) => createImageBitmap(blob))

    const bannerCanvas = document.createElement('canvas')
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = pdf.internal.pageSize.getHeight()
    const aspectRatio = pdfWidth / pdfHeight
    const canvasWidth = 1280
    const canvasHeight = canvasWidth / aspectRatio

    bannerCanvas.width = canvasWidth
    bannerCanvas.height = canvasHeight
    const ctx = bannerCanvas.getContext('2d')
    ctx?.drawImage(bannerImage, 0, 0, canvasWidth, canvasHeight)
    const bannerData = bannerCanvas.toDataURL('image/png')

    pdf.addImage(bannerData, 'PNG', 0, 0, pdfWidth, pdfHeight)

    const boxWidth = pdfWidth - 20
    pdf.setFontSize(20)
    const titleLines = pdf.splitTextToSize(raffleTitle, boxWidth - 20)
    const titleHeight = titleLines.length * (pdf.getLineHeight() || 10)

    pdf.setFontSize(14)
    const descriptionLines = pdf.splitTextToSize(
      raffleDescription,
      boxWidth - 20,
    )
    const descriptionHeight =
      descriptionLines.length * (pdf.getLineHeight() || 8)

    const hashBoxHeight = 20
    const totalBoxHeight = titleHeight + descriptionHeight + hashBoxHeight + 20
    const boxX = 10
    const boxY = pdfHeight - totalBoxHeight - 10

    pdf.setFillColor(255, 255, 255)
    pdf.roundedRect(boxX, boxY, boxWidth, totalBoxHeight, 5, 5, 'F')

    pdf.setFontSize(20)
    pdf.setTextColor(0, 0, 0)
    pdf.text(titleLines, boxX + 10, boxY + 15)

    pdf.setFontSize(14)
    pdf.text(descriptionLines, boxX + 10, boxY + titleHeight + 20)

    pdf.setFillColor(247, 247, 247)
    pdf.roundedRect(
      boxX + 10,
      boxY + totalBoxHeight - hashBoxHeight - 10,
      boxWidth - 20,
      hashBoxHeight,
      3,
      3,
      'F',
    )

    pdf.setFontSize(14)
    pdf.setTextColor(159, 159, 159)
    pdf.text(
      raffleHash.toUpperCase(),
      boxX + boxWidth / 2,
      boxY + totalBoxHeight - hashBoxHeight,
      { align: 'center', maxWidth: boxWidth - 30 },
    )

    const couponImage = await loadImage('/coupon.svg')
    const couponCanvas = document.createElement('canvas')
    couponCanvas.width = couponImage.naturalWidth
    couponCanvas.height = couponImage.naturalHeight
    const couponCtx = couponCanvas.getContext('2d')
    couponCtx?.drawImage(couponImage, 0, 0)
    const couponBackground = couponCanvas.toDataURL('image/png')

    const couponsPerPage = 26
    const couponChunks = []
    for (let i = 0; i < coupons.length; i += couponsPerPage) {
      couponChunks.push(coupons.slice(i, i + couponsPerPage))
    }

    couponChunks.forEach((chunk) => {
      pdf.addPage()
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const FIXED_COUPON_HEIGHT = 15
      const margin = 10
      const gap = 5

      const columns = chunk.length > 5 ? 2 : 1
      const couponWidth =
        (pageWidth - margin * 2 - gap * (columns - 1)) / columns

      // const rows = Math.ceil(chunk.length / columns)
      // const couponHeight = (pageHeight - margin * 2 - gap * (rows - 1)) / rows

      chunk.forEach((coupon, index) => {
        const col = index % columns
        const row = Math.floor(index / columns)
        const x = margin + col * (couponWidth + gap)
        const y = margin + row * (FIXED_COUPON_HEIGHT + gap)

        pdf.addImage(
          couponBackground,
          'PNG',
          x,
          y,
          couponWidth,
          FIXED_COUPON_HEIGHT,
        )

        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(isMobile ? 12 : 20)
        pdf.setTextColor(255, 255, 255)
        pdf.text(
          coupon.code,
          x + couponWidth / 5,
          y + FIXED_COUPON_HEIGHT / 2,
          {
            align: 'center',
            baseline: 'middle',
          },
        )
      })

      pdf.setFontSize(10)
      pdf.setTextColor(0, 0, 0)
      const footerText =
        'Caso tenha dúvidas sobre o sorteio ou a utilização dos cupons, entre em contato com nossa equipe de suporte.'
      const footerLines = pdf.splitTextToSize(
        footerText,
        pageWidth - margin * 2,
      )
      pdf.text(footerLines, pageWidth / 2, pageHeight - margin - 10, {
        align: 'center',
      })
    })

    const blob = pdf.output('blob')
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    return true
  } catch (error) {
    console.error('Erro ao gerar PDF:', error)
    return false
  }
}

export const GenerateRafflePdf = async ({
  raffleId,
  userId,
}: PDFGeneratorProps) => {
  const { isOther } = useOS()

  const data = await raffleService.getPrizeDrawHash(raffleId, userId)

  await generatePDF({
    coupons: data.coupons,
    isMobile: !isOther,
    raffleBanner: data.prizeDraw.banner,
    raffleDescription: data.prizeDraw.description,
    raffleHash: data.hash,
    raffleTitle: data.prizeDraw.name,
  })
}
